import React, { useState, useEffect, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import { Auth } from 'aws-amplify';
import { 
    Toolbar, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel, 
    Button, 
    Tabs, 
    Tab, 
    Box,
    Backdrop,
    CircularProgress,
    Alert,
    AlertTitle,
    TextField,
    Autocomplete
} from '@mui/material';
import * as Actions from '../../actions/actions';
import RouteSummaryGridPanel from './RouteSummaryGridPanel';
import RouteDetailsGridPanel from './RouteDetailsGridPanel';
import DoorDetailsGridPanel from './DoorDetailsGridPanel';

const LoadValidationDashboard = ({ filters, filterOptions, loading, error, pagination, sorting, customers, ...props }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [authToken, setAuthToken] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [availableDCs, setAvailableDCs] = useState([]);

    useLayoutEffect(() => {
        document.body.style.backgroundColor = "#fff";
    });

    useEffect(() => {
        Auth.currentSession().then(res => {
            let accessToken = res.getAccessToken();
            let jwt = accessToken.getJwtToken();
            setAuthToken(jwt);
            localStorage.setItem('authToken', jwt);
            
            // Load customers with DCs on mount
            props.fetchCustomersWithDC();
        });
    }, []);

    const handleSearch = () => {
        const hasRequiredFilters = filters.date && filters.customer && filters.dc;
        
        if (authToken && hasRequiredFilters) {
            props.loadRouteList(authToken, filters);
            props.loadDoorList(authToken, filters);
            props.loadRouteSummary(authToken, filters, pagination.routeSummary, sorting.routeSummary);
            props.loadRouteDetails(authToken, filters, pagination.routeDetails, sorting.routeDetails);
            props.loadDoorDetails(authToken, filters, pagination.doorDetails, sorting.doorDetails);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleFilterChange = (filterType) => (event) => {
        const value = event.target.value;
        
        // When customer changes, update available DCs
        if (filterType === 'customer') {
            const customer = customers.find(c => c.customer_id === value);
            setSelectedCustomer(customer);
            setAvailableDCs(customer ? customer.dcs || [] : []);
            // Reset DC filter when customer changes
            props.updateFilters('dc', '');
        }
        
        props.updateFilters(filterType, value);
    };

    const handleRouteFilterChange = (_event, value) => {
        props.updateFilters('route', value);
    };

    const handleDoorFilterChange = (_event, value) => {
        props.updateFilters('door', value);
    };

    const handleRefresh = () => {
        handleSearch();
    };

    const isLoading = loading.routeSummary || loading.routeDetails || loading.doorDetails;
    const currentError = activeTab === 0 ? error.routeSummary : activeTab === 1 ? error.routeDetails : error.doorDetails;
    const hasRequiredFilters = filters.date && filters.customer && filters.dc;

    return (
        <div style={{ width: '100%' }}>
            <div>
                <h3>Load Validation Dashboard</h3>
                
                {/* Shared Filters */}
                <Toolbar sx={{ justifyContent: "flex-start", minHeight: "30px !important", gap: 2 }}>
                    <TextField
                        label="Date"
                        type="date"
                        value={filters.date || ''}
                        onChange={handleFilterChange('date')}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: 200 }}
                    />

                    <FormControl sx={{ width: 200 }} size="small">
                        <InputLabel>Customer</InputLabel>
                        <Select
                            value={filters.customer || ''}
                            onChange={handleFilterChange('customer')}
                            label="Customer"
                        >
                            <MenuItem value="">
                                <em>Select Customer</em>
                            </MenuItem>
                            {(customers || []).map((customer) => (
                                <MenuItem key={customer.customer_id} value={customer.customer_id}>
                                    {customer.customer_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ width: 200 }} size="small">
                        <InputLabel>DC</InputLabel>
                        <Select
                            value={filters.dc || ''}
                            onChange={handleFilterChange('dc')}
                            label="DC"
                            disabled={!selectedCustomer}
                        >
                            <MenuItem value="">
                                <em>Select DC</em>
                            </MenuItem>
                            {availableDCs.map((dc) => (
                                <MenuItem key={dc.dc_id} value={dc.dc_id}>
                                    {dc.dc_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {activeTab === 1 && (
                        <Autocomplete
                            options={filterOptions.routes || []}
                            value={filters.route || null}
                            onChange={handleRouteFilterChange}
                            getOptionLabel={(option) => option.label || ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField {...params} label="Route" size="small" />}
                            sx={{ width: 200, backgroundColor: 'white' }}
                        />
                    )}
                    {activeTab === 2 && (
                        <Autocomplete
                            options={filterOptions.doors || []}
                            value={filters.door || null}
                            onChange={handleDoorFilterChange}
                            getOptionLabel={(option) => option.label || ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField {...params} label="Door" size="small" />}
                            sx={{ width: 200, backgroundColor: 'white' }}
                        />
                    )}
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={isLoading || !hasRequiredFilters}
                    >
                        Search
                    </Button>

                    <Button variant="outlined" onClick={handleRefresh} disabled={isLoading || !hasRequiredFilters}>
                        Refresh
                    </Button>
                </Toolbar>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        <Tab label="Route Summary" />
                        <Tab label="Route Details" />
                        <Tab label="Door Details" />
                    </Tabs>
                </Box>

                {/* Error State */}
                {currentError && (
                    <Alert severity="error" sx={{ margin: '20px' }}>
                        <AlertTitle>Error</AlertTitle>
                        {typeof currentError === 'string' ? currentError : 'Unable to load data. Please try again or contact support.'}
                        <Button onClick={handleRefresh} sx={{ marginTop: '10px' }}>
                            Retry
                        </Button>
                    </Alert>
                )}

                {/* Required Filters Warning */}
                {!hasRequiredFilters && !currentError && (
                    <Alert severity="info" sx={{ margin: '20px' }}>
                        <AlertTitle>Select Filters</AlertTitle>
                        Please select Date, Customer, and DC to view the dashboard data.
                    </Alert>
                )}

                {/* Tab Panels */}
                {!currentError && hasRequiredFilters && (
                    <>
                        {activeTab === 0 && <RouteSummaryGridPanel />}
                        {activeTab === 1 && <RouteDetailsGridPanel authToken={authToken} />}
                        {activeTab === 2 && <DoorDetailsGridPanel authToken={authToken} />}
                    </>
                )}

                {/* Loading Indicator */}
                <Backdrop
                    sx={{ color: '#000', backgroundColor: 'rgb(255,255,255,0.7)', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={isLoading}
                >
                    <p>Please wait, while data is loading...</p> <CircularProgress color="inherit" />
                </Backdrop>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        filters: state.loadValidationDashboard.filters,
        filterOptions: state.loadValidationDashboard.filterOptions,
        loading: state.loadValidationDashboard.loading,
        error: state.loadValidationDashboard.error,
        pagination: state.loadValidationDashboard.pagination,
        sorting: state.loadValidationDashboard.sorting,
        customers: state.edgePCs.customers || []
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        fetchCustomersWithDC: () => dispatch(Actions.LOAD_CUSTOMERS_WITH_DC()),
        loadRouteList: (authToken, filters) =>
            dispatch(Actions.LOAD_ROUTE_LIST(authToken, filters)),
        loadDoorList: (authToken, filters) =>
            dispatch(Actions.LOAD_DOOR_LIST(authToken, filters)),
        loadRouteSummary: (authToken, filters, pagination, sorting) => 
            dispatch(Actions.LOAD_ROUTE_SUMMARY(authToken, filters, pagination, sorting)),
        loadRouteDetails: (authToken, filters, pagination, sorting) => 
            dispatch(Actions.LOAD_ROUTE_DETAILS(authToken, filters, pagination, sorting)),
        loadDoorDetails: (authToken, filters, pagination, sorting) => 
            dispatch(Actions.LOAD_DOOR_DETAILS(authToken, filters, pagination, sorting)),
        updateFilters: (filterType, value) => dispatch(Actions.UPDATE_LVD_FILTERS(filterType, value))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LoadValidationDashboard);
